import { OfferLetterFormsService } from 'api-clients/monolith/services/OfferLetterFormsService';
import { useCallback } from 'react';

import { MutationOptions, useApiServiceMutation } from 'hooks/useApiServiceMutation';

export interface PreviewInput {
  offer_letter_template_id?: string;
  /** Template field key -> value. */
  field_values?: Record<string, string>;
  /**
   * Data key -> value (AXHE-4001). Readiness resolves the conditional sections
   * under these, so a required field that only lives inside a section the
   * answers exclude stops blocking the send — and one inside a section they
   * include starts being asked for.
   */
  condition_values?: Record<string, string>;
}

type PreviewData = Awaited<
  ReturnType<typeof OfferLetterFormsService.postInternalApiApplicantsOfferLetterFormPreview>
>;

/**
 * Re-evaluates approval guardrails + required-field readiness against the
 * recruiter's current entries (AXHE-3717 `preview`).
 *
 * AXHE-4233: accepts `MutationOptions` so the single "Preview document" click
 * can chain straight into create/update on a clean result, and surface an
 * inline error on a transport failure, without the container polling
 * `result` in an effect. `useApiServiceMutation`'s CancelError path calls
 * neither callback, which is exactly right for `invalidatePreview` cancelling
 * an in-flight check on edit — see index.tsx.
 */
export const useOfferPreview = (
  applicantExternalId: string,
  options?: MutationOptions<PreviewData, unknown>,
) => {
  const run = useCallback(
    (input: PreviewInput) =>
      OfferLetterFormsService.postInternalApiApplicantsOfferLetterFormPreview(
        applicantExternalId,
        input,
      ),
    [applicantExternalId],
  );

  const { mutation, result, reset } = useApiServiceMutation<PreviewData, typeof run>(run, options);

  // useApiServiceMutation.mutation() returns a cancel function at runtime
  // (`() => promise.cancel()`) but types it as void. Surface it typed here so
  // the caller can abort an in-flight preview when the recruiter edits before it
  // resolves — otherwise the late response clobbers the reset with a stale
  // "ready" for pre-edit values (AXHE-3719 review). Scoped to this hook rather
  // than widening the shared hook's return type (which many mocks depend on).
  const runPreview = useCallback(
    (input: PreviewInput): (() => void) => mutation(input) as unknown as () => void,
    [mutation],
  );

  return { mutation: runPreview, result, reset };
};
